// Additional security utilities for admin panel

// Rate limiting for login attempts
const LOGIN_ATTEMPTS_KEY = 'admin_login_attempts';
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

export const checkLoginAttempts = (): boolean => {
  const attempts = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
  if (!attempts) return true;

  const { count, timestamp } = JSON.parse(attempts);
  const now = Date.now();

  // Reset if lockout period has passed
  if (now - timestamp > LOCKOUT_DURATION) {
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    return true;
  }

  return count < MAX_LOGIN_ATTEMPTS;
};

export const recordLoginAttempt = (success: boolean): void => {
  if (success) {
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    return;
  }

  const attempts = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
  const now = Date.now();

  if (!attempts) {
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify({
      count: 1,
      timestamp: now
    }));
  } else {
    const { count, timestamp } = JSON.parse(attempts);
    
    // Reset if lockout period has passed
    if (now - timestamp > LOCKOUT_DURATION) {
      localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify({
        count: 1,
        timestamp: now
      }));
    } else {
      localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify({
        count: count + 1,
        timestamp
      }));
    }
  }
};

export const getRemainingLockoutTime = (): number => {
  const attempts = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
  if (!attempts) return 0;

  const { timestamp } = JSON.parse(attempts);
  const now = Date.now();
  const remaining = LOCKOUT_DURATION - (now - timestamp);
  
  return Math.max(0, Math.ceil(remaining / 1000 / 60)); // Return minutes
};

// Session management
export const createAdminSession = (): void => {
  const sessionData = {
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
    ip: 'client-side' // In a real app, this would come from server
  };
  sessionStorage.setItem('adminAuth', btoa(JSON.stringify(sessionData)));
};

export const validateAdminSession = (): boolean => {
  try {
    const sessionData = sessionStorage.getItem('adminAuth');
    if (!sessionData) return false;

    const decoded = JSON.parse(atob(sessionData));
    const now = Date.now();
    const sessionAge = now - decoded.timestamp;
    
    // Session expires after 2 hours
    const maxSessionAge = 2 * 60 * 60 * 1000;
    
    if (sessionAge > maxSessionAge) {
      sessionStorage.removeItem('adminAuth');
      return false;
    }

    // Basic user agent validation (optional security measure)
    if (decoded.userAgent !== navigator.userAgent) {
      sessionStorage.removeItem('adminAuth');
      return false;
    }

    return true;
  } catch {
    sessionStorage.removeItem('adminAuth');
    return false;
  }
};

export const clearAdminSession = (): void => {
  sessionStorage.removeItem('adminAuth');
};
